import { BehaviorSubject, EMPTY, merge, NEVER, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmBaseApiService } from './bcm-base-api.service';
import { BcmBaseState } from './bcm-base.state';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { FuseUtils } from '@fuse/utils';
import { DEFAULT_DEBOUNCE_TIME_SEARCH } from '@modules/bcm/@shared/constants';
import {
    parseHttpResponseItem,
    parseHttpResponseList,
    parseHttpResponsePage
} from '@shared/functions/parse-http-response';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { DataFilterService } from '@core/datafilter/data-filter.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { U2bColumnDefinition, U2bTableData } from '@core/components/layout/table/table.types';
import { FilesApiService } from '@modules/bcm/@shared/services';
import { IFile } from '@shared/interfaces/file';
import { isPlainObject } from '@shared/functions/is-plain-object';
import { FormArray, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { Page } from '@shared/models/pagination';
import {
    TableHeaderAction,
    TablePredefinedFilter,
    TableRowAction
} from '@modules/bcm/@core/state-management/bcm-base.types';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { FILTER_OPERATORS_DEFAULTS_BY_FIELD_TYPE } from '@core/datafilter/constants';
import { TranslationService } from '@core/translation/translation.service';
import { DataFilterOperationType } from '@core/datafilter/filter-operation-type';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { BcmBaseBulkChanges } from '@modules/bcm/@core/state-management/bcm-base-bulk-changes';
import { UserService } from '@core/services/user.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { downloadFile } from '@shared/functions/download-file';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { InformationDialogService } from '@sharedComponents/dialogs/information-dialog/information-dialog.service';
import { openFilteredListExportSalesDialog } from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import { FacadeTableUiRegistry } from '@modules/bcm/@core/state-management/bcm-facade-table-ui-registry';
import {
    resolveExportColumnsFromDisplayedColumns,
    resolveExportColumnsFromSettings,
    TableExportColumnDto,
} from '@modules/bcm/@core/state-management/table-export.util';
import { buildTableExportFileName, normalizeTableExportFileName, sanitizeFileNameBase } from '@modules/bcm/@core/state-management/table-export-filename.util';
import { TableExportColumnRegistry } from '@modules/bcm/@core/state-management/table-export-column-registry.service';

interface BaseEntity {
    id?: number;
}

const facadesMap = new Map<BcmUserTableSettingKey, BcmBaseFacade<BaseEntity, BaseEntity>>();

@Injectable()
export abstract class BcmBaseFacade<Entity extends BaseEntity, RawEntity = unknown> extends BcmBaseApiService<Entity, RawEntity> {

    private readonly _tableExportColumnRegistry = inject(TableExportColumnRegistry);

    static readonly reloadById$: Subject<{ key: BcmUserTableSettingKey, item: BaseEntity }> = new Subject<{
        key: BcmUserTableSettingKey,
        item: BaseEntity
    }>();

    protected readonly _userService = inject(UserService);
    protected readonly _bcmTenantService = inject(BcmTenantService);
    protected readonly _informationDialogService = inject(InformationDialogService);

    protected readonly _tolgee = inject(TolgeeService);

    private readonly state: BcmBaseState<Entity>;

    public readonly searchTerm$ = new BehaviorSubject<string>('');

    private _persistSearchTerm = true;

    /** Admin-only tables: column settings live in browser storage, not tenant BCM user-settings. */
    protected readonly useBrowserStoredUserTableSettings: boolean = false;

    public isSearchTermPersistenceEnabled(): boolean {
        return this._persistSearchTerm;
    }

    public usesBrowserStoredUserTableSettings(): boolean {
        return this.useBrowserStoredUserTableSettings;
    }

    protected readonly abstract resourcePath: string;

    /** Override to load the list from a different endpoint than {@link resourcePath}. */
    protected get listResourcePath(): string {
        return this.resourcePath;
    }

    public readonly abstract resourceNameSingular: string;

    public readonly abstract resourceNamePlural: string;

    // optional
    public readonly resourceNameSubHeadline: string;

    public readonly abstract resourceIconSingular: string;

    public readonly abstract resourceIconPlural: string;

    // optional
    public readonly resourceIconIsSVG?: boolean;

    public readonly abstract readPermission: BcmUserPermission;

    public readonly abstract writePermission: BcmUserPermission;

    public readonly abstract deletePermission: BcmUserPermission;

    // optional
    public readonly addNewButtonText: undefined | string;
    public readonly addNewCallback: undefined | (() => void);

    /** When set, page header uses Tolgee instead of {@link resourceNamePlural} */
    public readonly resourceNamePluralTranslationKey?: string;
    /** When set, used with {@link addNewButtonTextTranslationKey} for the add button label */
    public readonly resourceNameSingularTranslationKey?: string;
    /** When set, add button uses Tolgee instead of {@link addNewButtonText} */
    public readonly addNewButtonTextTranslationKey?: string;
    /** When set, used as the full add-button label (without "{entity} hinzufügen" suffix). */
    public readonly addNewButtonCaptionTranslationKey?: string;

    get resourceNamePluralDisplay(): string {
        if (this.resourceNamePluralTranslationKey) {
            return this._tolgee.translate(this.resourceNamePluralTranslationKey, undefined, this.resourceNamePlural);
        }
        return this.resourceNamePlural;
    }

    // optional
    public readonly headerActions: undefined | TableHeaderAction[];

    /**
     * When true, the generic "Als Excel exportieren" button in the page-table-facade
     * template is hidden. Use this when a facade provides its own dedicated export
     * action in headerActions (e.g. FinancialRecordsFacade).
     */
    public readonly hideGenericExcelButton?: boolean;

    // optional
    public readonly rowActions: undefined | TableRowAction<Entity>[];

    // optional
    public readonly predefinedFilter: undefined | TablePredefinedFilter<Entity>[];

    // optional
    public readonly customSortAccessor: undefined | ((item: Entity, property: string) => string | number);

    // optional
    public readonly bulkChanges?: BcmBaseBulkChanges<Entity>;

    // ------------------------------------

    private _searchTerm$ = this.searchTerm$
        .pipe(
            debounceTime(DEFAULT_DEBOUNCE_TIME_SEARCH),
            distinctUntilChanged()
        );

    public get hasServerSideFiltering(): boolean {
        return this.tableDataDefaults.hasServerSideFiltering;
    }

    public rowClickHandler: undefined | ((item: Entity) => void);

    /** Optional click handlers for specific table columns (property name → handler). */
    public columnCellClickHandlers?: Partial<Record<string, (item: Entity, event: MouseEvent) => void>>;

    public get isLoading$(): Observable<boolean> {
        return this.state.isUpdating$;
    }

    public get selected$(): Observable<Entity> {
        return this.state.selected$;
    }

    public get selected(): Entity {
        return this.state.selected;
    }

    public get list$(): Observable<Entity[]> {
        return this.state.list$;
    }

    public get page$(): Observable<Page<Entity>> {
        return this.state.page$;
    }

    public get filteredList$(): Observable<Entity[]> {
        const mergeSources: Observable<any>[] = [this._searchTerm$];

        if (this.dataFilterService) {
            mergeSources.push(this.dataFilterService.currentFilter$);
        }

        return merge(...mergeSources)
            .pipe(
                switchMap(() => {
                    const filterText = this.searchTerm$.getValue();
                    const currentFilter = this.dataFilterService?.currentFilter$.getValue();

                    return this.state.list$
                        .pipe(
                            switchMap(list => {

                                let filteredList = list;

                                if (currentFilter) {
                                    filteredList = currentFilter.dataFilter.match(list);
                                }

                                return of(FuseUtils.filterArrayByString(filteredList, filterText));
                            })
                        );
                }),
            );
    }

    private _formGroup: UntypedFormGroup;

    public get formGroup(): UntypedFormGroup {
        return this._formGroup;
    }

    public get hasRegisteredFormGroup(): boolean {
        return !!this._formGroup;
    }

    public get hasPristineFormGroup(): boolean {
        return this._formGroup?.pristine;
    }

    public get canWrite(): boolean {
        return this._userService.checkPermission(this.writePermission);
    }

    public get canDelete(): boolean {
        return this._userService.checkPermission(this.deletePermission);
    }

    public dataFilterService!: DataFilterService<Entity>;

    public get tableId(): BcmUserTableSettingKey {
        return this.tableDataDefaults.tableId;
    }

    public get columnDefinitions(): U2bColumnDefinition[] {
        return this.tableDataDefaults.columnDefinitions || [];
    }

    public get activeSortColumn(): string | undefined {
        return this.tableDataDefaults.activeSortColumn;
    }

    public get activeSortDirection(): 'asc' | 'desc' | undefined {
        return this.tableDataDefaults.activeSortDirection;
    }

    public get tableCellLineClamp(): number | undefined {
        return this.tableDataDefaults.tableCellLineClamp;
    }

    public get uniqueIdentAttribute(): string {
        return this.tableDataDefaults.uniqueIdentAttribute || 'id';
    }

    private _lastListHttpParams: HttpParams;

    private _lastPageHttpParamsKey: string | null = null;

    // "back to the list" is served from state while the params are unchanged; only large lists opt in (#7262)
    protected readonly cacheListPages: boolean = false;

    private buildHttpParamsCacheKey(params: HttpParams): string {
        return params.keys()
            .sort()
            .map(key => `${key}=${(params.getAll(key) || []).join(',')}`)
            .join('&');
    }

    protected constructor(private modelClass: any,
                          private tableDataDefaults: U2bTableData,
                          protected bcmNavigationService: BcmNavigationService,
                          protected appNotificationService: AppNotificationService,
                          protected confirmDialogService: ConfirmDialogService,
                          private bcmUserSettingsFacade: BcmUserSettingsFacade, // todo: implement filter saving
                          protected httpClient: HttpClient,
                          protected bcmService: BcmService,
                          private filesApiService?: FilesApiService,
    ) {
        super(httpClient, bcmService);

        if (typeof modelClass !== 'function') {
            throw TypeError(`"${modelClass}" is not of type ctor.`);
        }

        facadesMap.set(tableDataDefaults.tableId, this);

        inject(FacadeTableUiRegistry).register({
            resetTableUiState: () => {
                this._persistSearchTerm = false;
                this.searchTerm$.next('');
                this.dataFilterService?.resetActiveFilters(true);
                this._persistSearchTerm = true;
            },
        });

        this.state = new BcmBaseState<Entity>(this.uniqueIdentAttribute);

        // needs to be done before DataFilterService is instantiated
        this.prepareColumnDefinitions();

        this.dataFilterService = new DataFilterService<Entity>(
            tableDataDefaults.tableId,
            this.columnDefinitions,
            this.hasServerSideFiltering,
            () => this.bcmService.tenant?.id,
        );

        BcmBaseFacade.reloadById$.subscribe(data => {
            const facade = facadesMap.get(data.key) as BcmBaseFacade<Entity, RawEntity>;

            if (facade) {
                facade.updateStateFor(data.item as Entity);
            }
        });
    }

    // todo: maybe we need more than one? => updateStateFor(...item: Entity[])
    public updateStateFor(item: Entity, markFormAsDirty = false) {
        if (item) {
            this.state.updateStateFor(item);

            if (markFormAsDirty) {
                this.markFormAsDirty();
            }
        }
    }

    public saveLastUnsavedFormState() {
        if (!this.hasRegisteredFormGroup) {
            throw Error('<facade>.saveLastUnsavedFormState() can only be called if a formGroup was registered.');
        }
        if (!this._userService.checkPermission(this.writePermission)) {
            this.appNotificationService.showError(TranslationService.translate('common.error.noSavePermission', { permission: this.resourceNamePluralDisplay }));
            return;
        }
        if (this._formGroup.invalid) {
            this._formGroup.markAllAsTouched();
            this.appNotificationService.showError(TranslationService.translate('common.checkFieldsHighlight'));
            return;
        }
        return this.addOrUpdate(this.state.selected, this._formGroup.value)
            .subscribe(() => this._formGroup.markAsPristine());
    }

    public registerCurrentFormGroup(formGroup: UntypedFormGroup) {
        this._formGroup = formGroup;
    }

    public markFormAsDirty() {
        this._formGroup.markAsDirty();
    }

    public createEmptyModelInstance(): Observable<Entity> {
        const selected = new this.modelClass();
        this.state.setSelectedOnly(selected);
        this.state.unsetUpdating();
        return of(selected);
    }

    // on setting a filter, all needs to get reset and new list/page needs to be loaded
    public setPredefinedFilter(predefinedFilter?: TablePredefinedFilter<Entity>) {
        this.dataFilterService.resetActiveFilters();

        if (predefinedFilter?.filters) {
            for (const filterRaw of Object.values(predefinedFilter.filters)) {
                const foundFormArray = this.dataFilterService.allFiltersFormGroup.get(filterRaw.property) as FormArray;

                if (foundFormArray) {
                    foundFormArray.push(new FormGroup({
                        operator: new FormControl(filterRaw.operator || filterRaw.columnDefinition.filter.operators[0]),
                        value: new FormControl(filterRaw.value),
                        operationType: new FormControl(filterRaw.operationType || DataFilterOperationType.And),
                        columnDefinition: new FormControl(filterRaw.columnDefinition)
                    }));
                }
            }
        }

        this.dataFilterService.forceNextFormValueChange();
        this.dataFilterService.allFiltersFormGroup.updateValueAndValidity();
    }

    public loadList(httpParams = new HttpParams()): Observable<Entity[]> {
        this.state.setUpdating();

        this._lastListHttpParams = httpParams;

        return this.getList(this.listResourcePath, httpParams)
            .pipe(
                parseHttpResponseList<RawEntity, Entity>(this.modelClass),
                tap(list => this.state.setList(list)),
                finalize(() => this.state.unsetUpdating())
            );
    }

    public loadPage(httpParams: HttpParams = new HttpParams(), options?: { force?: boolean }): Observable<Page<Entity>> {
        const paramsKey = this.buildHttpParamsCacheKey(httpParams);
        const cachedPage = this.state.page;

        if (this.cacheListPages
            && !options?.force
            && this._lastPageHttpParamsKey === paramsKey
            && cachedPage?.results?.length) {
            return of(cachedPage);
        }

        this.state.setUpdating();

        this._lastListHttpParams = httpParams;

        return this.getPage(this.listResourcePath, httpParams)
            .pipe(
                parseHttpResponsePage<RawEntity, Entity>(this.modelClass),
                map(pageResult => this.mapLoadedPage(pageResult, httpParams)),
                tap(pageResult => {
                    this._lastPageHttpParamsKey = paramsKey;
                    this.state.setPage(pageResult);
                }),
                finalize(() => this.state.unsetUpdating())
            );
    }

    // Hook for subclasses to transform a fetched page (e.g. apply a client-side
    // sort) before it lands in state and reaches subscribers. Receives the http
    // params used for the request so subclasses can honor the active sort.
    protected mapLoadedPage(page: Page<Entity>, _httpParams: HttpParams): Page<Entity> {
        return page;
    }

    public reloadCurrentListOrPage() {
        if (this.hasServerSideFiltering) {
            this.loadPage(this._lastListHttpParams).subscribe();
        } else {
            this.loadList(this._lastListHttpParams).subscribe();
        }
    }

    /**
     * Checks module license (upsell dialog) and user permission before filtered list export.
     */
    protected guardFilteredListExportOrAbort(): boolean {
        if (!this._bcmTenantService.checkPermission(BcmTenantPermission.FILTERED_LIST_EXPORT)) {
            openFilteredListExportSalesDialog(this._informationDialogService);
            return false;
        }

        if (!this._userService.checkPermission(BcmUserPermission.EXPORT_FILTERED_VIEW)) {
            this.appNotificationService.showError(TolgeeService.translate('common.error.noPermission'));
            return false;
        }

        return true;
    }

    /**
     * Exports the currently filtered & visible table columns as an Excel file.
     * Sends the active filter params + the columns shown at the current breakpoint to the
     * generic backend endpoint GET /export/excel/table/:resource.
     */
    public exportTableAsExcel(): void {
        if (!this.supportsTableExcelExport()) {
            console.warn('exportTableAsExcel is only supported for server-side filtered tables.');
            return;
        }

        if (!this.guardFilteredListExportOrAbort()) {
            return;
        }

        const activeColumns = this.resolveActiveExportColumns();
        const exportLabel = sanitizeFileNameBase(this.resourceNamePlural);
        const downloadBaseName = buildTableExportFileName(exportLabel);

        const filterParams = this.buildTableExportFilterParams()
            .set('columns', JSON.stringify(activeColumns))
            .set('workSheetName', 'Export')
            .set('fileName', downloadBaseName);

        this.getBlob(`export/excel/table/${this.getTableExportResourceKey()}`, filterParams)
            .subscribe(blob => downloadFile(blob, normalizeTableExportFileName(downloadBaseName), 'xlsx'));
    }

    /**
     * Override when export uses a different resource key than {@link resourcePath}
     * (e.g. mail-messages vs messages/mails).
     */
    protected getTableExportResourceKey(): string {
        return this.resourcePath;
    }

    /**
     * Override when the list has no server-side paging but still supports filtered export.
     */
    protected supportsTableExcelExport(): boolean {
        return this.hasServerSideFiltering;
    }

    /**
     * Builds HTTP params for export (filters, sort, search) without pagination.
     */
    protected buildTableExportFilterParams(): HttpParams {
        return (this._lastListHttpParams || new HttpParams())
            .delete('page')
            .delete('size');
    }

    /**
     * Resolves visible export columns from saved user table settings (same logic as the table UI).
     */
    protected resolveActiveExportColumns(): TableExportColumnDto[] {
        const liveColumns = this._tableExportColumnRegistry.getColumnDefinitions(this.tableId);
        if (liveColumns?.length) {
            const breakpoint = this._tableExportColumnRegistry.getBreakpoint(this.tableId);
            const displayed = this._tableExportColumnRegistry.getDisplayedColumnProperties(this.tableId, breakpoint);

            return resolveExportColumnsFromDisplayedColumns(liveColumns, displayed, breakpoint);
        }

        const userSettings = this.bcmUserSettingsFacade.getCurrentSettings()?.[this.tableId]?.columnDefinitions;

        return resolveExportColumnsFromSettings(this.columnDefinitions, userSettings);
    }

    public reloadCurrentPage(options?: { force?: boolean }) {
        if (this.hasServerSideFiltering) {
            this.loadPage(this._lastListHttpParams, { force: options?.force ?? true }).subscribe();
        }
    }

    public loadById(id: number, params = new HttpParams()): Observable<Entity> {
        this.state.setUpdating();

        if (!id) {
            return this.createEmptyModelInstance();
        }

        return this.get(`${this.resourcePath}/${id}`, params)
            .pipe(
                parseHttpResponseItem<RawEntity, Entity>(this.modelClass),
                tap(selected => this.state.setSelected(selected)),
                finalize(() => this.state.unsetUpdating())
            );
    }

    public reloadSelected(givenEntity?: Entity): Subscription {
        this.state.setUpdating();
        const selected = givenEntity || this.state.selected;
        return (
            selected && selected[this.uniqueIdentAttribute]
                ? this.get(`${this.resourcePath}/${selected[this.uniqueIdentAttribute]}`)
                : NEVER
        )
            .pipe(
                parseHttpResponseItem<RawEntity, Entity>(this.modelClass),
                tap(loadedItem => this.state.updateStateFor(loadedItem)),
                finalize(() => this.state.unsetUpdating())
            ).subscribe();
    }

    public add(item: Entity, params = new HttpParams(), navigateToAddedItem = true): Observable<Entity> {

        if (item) {

            this.state.setUpdating();

            return this.post(this.resourcePath, item, params)
                .pipe(
                    parseHttpResponseItem<RawEntity, Entity>(this.modelClass),
                    tap((addedItem) => {
                        this.state.addToList(addedItem);
                        // the new record is not in the cached page
                        this._lastPageHttpParamsKey = null;
                        this.appNotificationService.showSuccess(TranslationService.translate('common.notify.recordSaved'));

                        if (navigateToAddedItem) {
                            this.bcmNavigationService.navigate([this.resourcePath, addedItem[this.uniqueIdentAttribute]]);
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return throwError(error);
                    }),
                    finalize(() => this.state.unsetUpdating())
                );
        }

        return throwError(TranslationService.translate('common.checkYourEntries'));
    }

    public update(item: Entity, newItemData: Partial<Entity>, params = new HttpParams()): Observable<Entity> {

        if (item[this.uniqueIdentAttribute]) {

            this.state.setUpdating();

            const newItem = {
                ...item,
                ...newItemData
            };

            return this.put(`${this.resourcePath}/${item[this.uniqueIdentAttribute]}`, newItem, params)
                .pipe(
                    parseHttpResponseItem<RawEntity, Entity>(this.modelClass),
                    tap((updatedItem) => {
                        this.state.updateStateFor(updatedItem);
                        this.appNotificationService.showSuccess(TranslationService.translate('common.notify.recordSaved'));
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return throwError(error);
                    }),
                    finalize(() => this.state.unsetUpdating())
                );
        }

        return throwError(TranslationService.translate('common.checkYourEntries'));
    }

    public addOrUpdate(item: Entity, newItemData: Partial<Entity>, params?: HttpParams): Observable<Entity> {
        if (item[this.uniqueIdentAttribute]) {
            return this.update(item, newItemData, params);
        } else {
            return this.add({...item, ...newItemData}, params);
        }
    }

    public remove(item: Entity, name?: string, afterDeletionRedirectTo?: string): Observable<Entity> {

        if (!this._userService.checkPermission(this.deletePermission)) {
            this.appNotificationService.showError(TranslationService.translate('common.error.noDeletePermission', { permission: this.resourceNamePluralDisplay }));
            return EMPTY;
        }

        name = name || this.getNameLike(item) || this.resourceNameSingular;

        // if it's null, it's by intention!
        if (afterDeletionRedirectTo === undefined) {
            afterDeletionRedirectTo = this.resourcePath;
        }

        return this.confirmDialogService
            .useWarnTheme()
            .setTitle(TranslationService.translate('common.confirm.removeNamedTitle', { name }))
            .setBody(TranslationService.translate('common.confirm.irreversibleRemoveNamedBody', { name }))
            .openAndReturnResult()
            .pipe(
                filter(result => result === true),
                switchMap(() => {
                    this.state.setUpdating();
                    return this.delete(`${this.resourcePath}/${item[this.uniqueIdentAttribute]}`);
                }),
                tap((response: any) => {
                    if (response) {
                        this.state.removeFromList(item);
                        this.reloadCurrentPage();
                        if (afterDeletionRedirectTo) {
                            this.bcmNavigationService.navigate(afterDeletionRedirectTo);
                        }
                        this.appNotificationService.showSuccess(TranslationService.translate('common.notify.resourceDeleted', { resource: this.resourceNameSingular }));
                    }
                }),
                catchError(() => {
                    this.appNotificationService.showError(TranslationService.translate('common.error.resourceDeleteFailed', { resource: this.resourceNameSingular }));
                    return EMPTY;
                }),
                finalize(() => this.state.unsetUpdating())
            );
    }

    public removeMultiple(items: Entity[], afterDeletionRedirectTo = this.resourcePath): Observable<number[]> {

        if (!this._userService.checkPermission(this.deletePermission)) {
            this.appNotificationService.showError(TranslationService.translate('common.error.noDeletePermission', { permission: this.resourceNamePluralDisplay }));
            return EMPTY;
        }

        const names = items.map(item => this.getNameLike(item));
        const uniqueIdentAttributeList = items.map(item => item[this.uniqueIdentAttribute]);

        return this.confirmDialogService
            .useWarnTheme()
            .setTitle(TranslationService.translate('common.confirm.removePluralTitle', {
                resourcePlural: this.resourceNamePlural
            }))
            .setBody(
                TranslationService.translate('common.confirm.removeMultipleIntroHtml', {
                    resourcePlural: this.resourceNamePlural
                }) +
                `<ul>${names.map(name => `<li>${name}</li>`).join('')}</ul>`
            )
            .openAndReturnResult()
            .pipe(
                filter(result => result === true),
                switchMap(() => {
                    this.state.setUpdating();
                    return this.deleteMultiple(`${this.resourcePath}/${uniqueIdentAttributeList.join(',')}`);
                }),
                tap((deletedIdentifiers: number[]) => {
                    if (deletedIdentifiers) {
                        this.state.removeFromList(...items);
                        this.reloadCurrentPage();
                        this.appNotificationService.showSuccess(TranslationService.translate('common.notify.resourcesDeleted', { resource: this.resourceNamePlural }));
                    }
                }),
                catchError(() => {
                    this.appNotificationService.showError(TranslationService.translate('common.error.resourcesDeleteFailed', { resource: this.resourceNamePlural }));
                    return EMPTY;
                }),
                finalize(() => this.state.unsetUpdating())
            );
    }

    public loadFile(fileId: number, params: HttpParams = new HttpParams()) {
        if (!this.filesApiService) {
            throw new ReferenceError(`Please provide FilesApiService in ${this.resourcePath} facade to load file.`);
        }
        return this.filesApiService.getById(fileId, params);
    }

    public addOrUpdateFile(file: IFile, params: HttpParams = new HttpParams()): Observable<IFile> {
        if (!this.filesApiService) {
            throw new ReferenceError(`Please provide FilesApiService in ${this.resourcePath} facade to add or update a file.`);
        }
        if (file?.id) {
            return this.filesApiService.updateAndFetch(file, params);
        } else {
            return this.filesApiService.addAndFetch(file, params);
        }
    }

    public deleteFile(file: IFile, params: HttpParams = new HttpParams()): Observable<any> {
        if (!this.filesApiService) {
            throw new ReferenceError(`Please provide FilesApiService in ${this.resourcePath} facade to delete file.`);
        }
        return this.filesApiService.deleteById(file?.id, params);
    }

    public getNameLike(item: Entity): string | null {
        if (!item || !isPlainObject(item)) {
            return null;
        }
        if (item.hasOwnProperty('fullNameBackward')) {
            return item['fullNameBackward'];
        }
        if (item.hasOwnProperty('fullName')) {
            return item['fullName'];
        }
        if (item.hasOwnProperty('name')) {
            return item['name'];
        }
        if (item.hasOwnProperty('title')) {
            return item['title'];
        }
        if (item.hasOwnProperty('handle')) {
            return item['handle'];
        }
        return item.toString();
    }

    private prepareColumnDefinitions() {
        this.tableDataDefaults.columnDefinitions = this.tableDataDefaults.columnDefinitions
            .filter(columnDefinition => !('excludeIf' in columnDefinition) || !columnDefinition.excludeIf(this.bcmService.tenant))
            .map(columnDefinition => {

                if (columnDefinition.translationKey) {
                    columnDefinition.name = TranslationService.translate(columnDefinition.translationKey);
                } else {
                    columnDefinition.name = TranslationService.translate(columnDefinition.name);
                }

                if (columnDefinition.filter) {
                    if (!columnDefinition.filter.property) {
                        columnDefinition.filter.property = columnDefinition.property;
                    }

                    if (!columnDefinition.filter.fieldType) {
                        columnDefinition.filter.fieldType = FilterFieldType.Text;
                    }

                    if (!columnDefinition.filter.operators?.length) {
                        columnDefinition.filter.operators = FILTER_OPERATORS_DEFAULTS_BY_FIELD_TYPE[columnDefinition.filter.fieldType];
                    }

                    if (columnDefinition.filter.fieldType === FilterFieldType.Select
                        && !columnDefinition.filter.selectOptionsRelation
                        && !columnDefinition.filter.selectOptions) {
                        throw Error(`missing data for selectOptions in column definitions of column ${columnDefinition.property}`);
                    }
                }

                return columnDefinition;
            });
    }
}
